import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Loading from '../../components/common/Loading';
import Title from '../../components/common/Title';
import { apiRequest, apiFormRequest } from '../../utils';
import AccreditationForm from './AccreditationForm';
import { StatusPill } from './SupplierEvent';
import './Supplier.css';

/* A crew member's own details for the venue — name, ID, a scan — filled in once and reused on
   every event they crew. Reached from the link in the request mail / WhatsApp, or from the
   banner on the crew home. */
const CrewDetails = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const eventId = params.get('event') || '';
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const q = eventId ? `?event=${eventId}` : '';

  const load = useCallback(() => {
    apiRequest('GET', `/crew-details${q}`).then(setData).catch(() => setError('opsNoCrewRequest'));
  }, [q]);
  useEffect(load, [load]);

  const run = async (promise, okKey) => {
    setBusy(true);
    setFlash(null);
    try {
      const next = await promise;
      if (next && next.event) setData(next);
      setFlash({ ok: true, key: okKey });
    } catch (e) {
      if (e.data && e.data.event) setData(e.data);
      setFlash({ ok: false, key: (e.data && e.data.error) || 'opsSaveFailed' });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Base activeNav="home"><Card><p className="ops-hint">{t(error)}</p></Card></Base>;
  if (!data) return <Base activeNav="home"><Loading /></Base>;

  return (
    <Base activeNav="home">
      <Title>{t('opsMyDetails')}</Title>
      <p className="ops-sub">{[data.event.name, data.event.date, data.event.venue].filter(Boolean).join(' · ')}</p>
      <Card>
        <div className="ops-status">
          <StatusPill request={data.request} />
          {data.request.deadline && <span>{t('opsDeadline', { date: data.request.deadline })}</span>}
        </div>
        {data.request.status === 'confirmed'
          ? <p className="ops-ok">{t('opsAllConfirmed')}</p>
          : data.problems.length > 0 && <ul className="ops-problems">{data.problems.map((p) => <li key={p}>{p}</li>)}</ul>}
      </Card>
      {flash && <div className={`ops-flash ${flash.ok ? 'is-ok' : 'is-bad'}`}>{t(flash.key)}</div>}
      <Card>
        <p className="ops-hint">{t('opsCrewIntro')}</p>
        <AccreditationForm key={JSON.stringify(data.profile && data.profile.values)}
                           fields={data.fields} initial={data.profile} busy={busy}
                           onSubmit={(form) => run(apiFormRequest(`/crew-details${q}`, form), 'opsSaved')} />
      </Card>
      <div className="ops-confirm-bar">
        <button type="button" className="ops-btn ops-btn--big" disabled={busy || data.problems.length > 0}
                onClick={() => run(apiRequest('POST', `/crew-details/confirm${q}`), 'opsThanksConfirmed')}>
          {data.request.status === 'confirmed' ? t('opsConfirmAgain') : t('opsConfirm')}
        </button>
      </div>
    </Base>
  );
};

export default CrewDetails;
